import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import ResultDetail from './ResultDetail';
import { showToast } from '../layout/ToastContainer';

function formatDate(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function ResultsList() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [csvLoading, setCsvLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [viewArchived, setViewArchived] = useState(false);

  const load = useCallback(async (archived = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/results${archived ? '?archived=true' : ''}`);
      setResults(data.results);
    } catch {
      showToast('Failed to load results', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(viewArchived); }, [load, viewArchived]);

  async function viewFull(id) {
    try {
      const { data } = await api.get(`/results/${id}`);
      setSelected(data);
    } catch {
      showToast('Failed to load result', 'error');
    }
  }

  function handleDetailUpdate(updatedRecord) {
    // If archived state changed, remove from current view
    if (updatedRecord.archived !== viewArchived) {
      setResults(prev => prev.filter(r => r.id !== updatedRecord.id));
      setSelected(null);
      return;
    }
    setResults(prev => prev.map(r => r.id !== updatedRecord.id ? r : {
      ...r,
      done: updatedRecord.done,
      doneAt: updatedRecord.doneAt,
      archived: updatedRecord.archived,
      archivedAt: updatedRecord.archivedAt,
      emailLog: updatedRecord.emailLog || [],
      imageCount: (updatedRecord.images || []).length
    }));
    setSelected(updatedRecord);
  }

  function promptDelete(id) {
    setSelected(null);
    setConfirmDelete(id);
  }

  async function doDelete(id) {
    setDeleting(id);
    try {
      await api.delete(`/results/${id}`);
      setResults(prev => prev.filter(r => r.id !== id));
      setCheckedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      setConfirmDelete(null);
      showToast('Result deleted', 'success');
    } catch {
      showToast('Failed to delete', 'error');
    } finally {
      setDeleting(null);
    }
  }

  async function bulkArchive(unarchive = false) {
    if (checkedIds.size === 0) return;
    setArchiving(true);
    try {
      await api.post('/results/bulk-archive', { ids: Array.from(checkedIds), unarchive });
      setResults(prev => prev.filter(r => !checkedIds.has(r.id)));
      const n = checkedIds.size;
      setCheckedIds(new Set());
      showToast(`${n} result${n !== 1 ? 's' : ''} ${unarchive ? 'unarchived' : 'archived'}`, 'success');
    } catch {
      showToast('Archive action failed', 'error');
    } finally {
      setArchiving(false);
    }
  }

  const filtered = results.filter(r =>
    !search || [r.name, r.email, r.phone, r.ip, r.isp, r.browser, r.os]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  function toggleCheck(id) {
    setCheckedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  function toggleAll() {
    if (checkedIds.size === filtered.length && filtered.length > 0) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(filtered.map(r => r.id)));
    }
  }

  async function downloadCSV() {
    if (checkedIds.size === 0) return;
    setCsvLoading(true);
    try {
      const token = localStorage.getItem('speedtest_token');
      const response = await fetch('/api/results/csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(checkedIds) })
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : 'feedback-export.csv';
      a.click();
      URL.revokeObjectURL(url);
      showToast(`Exported ${checkedIds.size} record${checkedIds.size !== 1 ? 's' : ''}`, 'success');
    } catch {
      showToast('CSV export failed', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const allChecked = filtered.length > 0 && checkedIds.size === filtered.length;
  const someChecked = checkedIds.size > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.05em' }}>
            {viewArchived ? 'Archived Results' : 'Diagnostic Test Results'}
          </h1>
          <div style={{ color: '#9aa3b0', fontSize: 13, marginTop: 4 }}>
            {results.length} total record{results.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <a href="/" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
            Viewer Feedback &nbsp;&uarr;
          </a>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setViewArchived(v => !v); setCheckedIds(new Set()); setSearch(''); }}
              style={{ color: viewArchived ? 'var(--accent-orange)' : '#d0d8e4', borderColor: viewArchived ? 'var(--accent-orange)' : '#3e4a5a' }}>
              {viewArchived ? '← Active Results' : '⊘ Archived'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => load(viewArchived)} disabled={loading}
              style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
              {loading ? <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : '↻ Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Search + Bulk Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          className="input-field"
          placeholder="Search by name, email, IP, browser..."
          value={search}
          onChange={e => { setSearch(e.target.value); setCheckedIds(new Set()); }}
          style={{ maxWidth: 360 }}
        />
        {someChecked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{checkedIds.size} selected</span>
            {viewArchived ? (
              <button className="btn btn-ghost btn-sm" onClick={() => bulkArchive(true)} disabled={archiving}
                style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
                {archiving ? 'Unarchiving...' : '↩ Unarchive'}
              </button>
            ) : (
              <button className="btn btn-ghost btn-sm" onClick={() => bulkArchive(false)} disabled={archiving}
                style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
                {archiving ? 'Archiving...' : '⊘ Archive'}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={csvLoading}
              style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
              {csvLoading ? 'Exporting...' : '↓ CSV'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setCheckedIds(new Set())}
              style={{ color: '#9aa3b0', borderColor: '#3e4a5a' }}>Clear</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
            {search ? 'No results match your search.' : viewArchived ? 'No archived results.' : 'No diagnostic test results yet.'}
          </div>
        ) : (
          <table className="data-table" style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: 'center' }}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll}
                    style={{ cursor: 'pointer', accentColor: 'var(--accent-orange)' }} />
                </th>
                <th>Date</th>
                <th>Name</th>
                <th>Email</th>
                <th style={{ textAlign: 'right' }}>Down↓</th>
                <th style={{ textAlign: 'right' }}>Up↑</th>
                <th style={{ textAlign: 'right' }}>Ping</th>
                <th style={{ textAlign: 'right' }}>Jitter</th>
                <th>Browser</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{ opacity: r.done ? 0.7 : 1 }}>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={checkedIds.has(r.id)} onChange={() => toggleCheck(r.id)}
                      style={{ cursor: 'pointer', accentColor: 'var(--accent-orange)' }} />
                  </td>
                  <td style={{ color: '#b8c0cc', whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  <td style={{ color: '#c8d0dc' }}>{r.email}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-orange)', fontWeight: 600 }}>
                    {r.download.toFixed(1)} <span style={{ color: '#9aa3b0', fontWeight: 400 }}>Mbps</span>
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-orange)', fontWeight: 600 }}>
                    {r.upload.toFixed(1)} <span style={{ color: '#9aa3b0', fontWeight: 400 }}>Mbps</span>
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>
                    {r.ping.toFixed(0)} <span style={{ color: '#9aa3b0' }}>ms</span>
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>
                    {r.jitter.toFixed(1)} <span style={{ color: '#9aa3b0' }}>ms</span>
                  </td>
                  <td style={{ color: '#c8d0dc' }}>{r.browser}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {r.done && (
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#00c88c', background: 'rgba(0,200,140,0.12)', padding: '2px 7px', borderRadius: 10, display: 'inline-block' }}>
                          ✓ DONE
                        </span>
                      )}
                      {r.emailLog && r.emailLog.length > 0 && (
                        <span style={{ fontSize: 10, letterSpacing: '0.04em', color: '#7db8f7', background: 'rgba(100,160,255,0.12)', padding: '2px 7px', borderRadius: 10, display: 'inline-block' }}>
                          ✉ {formatDateShort(r.emailLog[r.emailLog.length - 1].sentAt)}
                        </span>
                      )}
                      {(!r.done && (!r.emailLog || r.emailLog.length === 0)) && (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => viewFull(r.id)}
                        style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>View</button>
                      <button className="btn btn-danger btn-sm" onClick={() => promptDelete(r.id)} disabled={deleting === r.id}>
                        {deleting === r.id ? '...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <ResultDetail record={selected} onClose={() => setSelected(null)} onDelete={promptDelete} onUpdate={handleDetailUpdate} />
      )}

      {confirmDelete && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>Confirm Delete</h2></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
                Are you sure you want to permanently delete this result? This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={() => doDelete(confirmDelete)} disabled={deleting === confirmDelete}>
                {deleting === confirmDelete ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
